import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PrincipalService } from '@ms/core';
import { EventManager } from '@ms/core/service/event-manager.service';
import { patchState, signalStore, withComputed, withHooks, withMethods } from '@ngrx/signals';
import {
    addEntity,
    removeAllEntities,
    removeEntity,
    setAllEntities,
    updateEntity,
    withEntities,
} from '@ngrx/signals/entities';
import { Subscription } from 'rxjs';
import { GradeLevel } from '../grade-levels/model/grade-level.model';

export const GradeLevelStore = signalStore(
    { providedIn: 'root' },
    withDevtools('campus-grade-level'),
    withEntities<GradeLevel>(),
    // To use localstorage : https://www.npmjs.com/package/@angular-architects/ngrx-toolkit
    //withStorageSync('campus-custom-parameter'),
    withComputed(store => ({
        size: computed(() => store.entities().length),
        loaded: signal<boolean>(false),
        roles: signal<string[]>([]),
        event: signal<Subscription>(undefined),
    })),
    withMethods(store => {
        const em = inject(EventManager);
        const principal = inject(PrincipalService);
        return {
            set(items: GradeLevel[]) {
                store.loaded.set(items.length > 0);
                patchState(store, setAllEntities([...items]));
            },

            add(item: GradeLevel) {
                if (!store.entityMap()[item.id]) {
                    patchState(store, addEntity({ ...item }));
                } else {
                    patchState(store, updateEntity({ id: item.id, changes: { ...item } }));
                }
                store.loaded.set(true);
            },

            remove(id: string) {
                patchState(store, removeEntity(id));
                store.loaded.set(true);
            },

            get(id: string): GradeLevel {
                return { ...store.entityMap()[id] };
            },

            update(item: GradeLevel) {
                console.log('GRADE_UPDATE', item);
                patchState(store, updateEntity({ id: item.id, changes: { ...item } }));
                store.loaded.set(true);
            },

            findAll(): GradeLevel[] {
                const result = [];
                store.ids().forEach(id => {
                    const item: GradeLevel = store.entityMap()[id];
                    result.push({ ...item });
                });
                return result.sort((s1, s2) => s1.order - s2.order);
            },

            findAllActive(): GradeLevel[] {
                const result = [];
                store.ids().forEach(id => {
                    const item: GradeLevel = store.entityMap()[id];
                    if (item.active == true) {
                        result.push({ ...item });
                    }
                });
                em.httpEnd();
                return result.sort((s1, s2) => s1.order - s2.order);
            },

            findBySchoolId(schoolId: string): GradeLevel[] {
                const result = [];
                store.ids().forEach(id => {
                    const i: GradeLevel = store.entityMap()[id];
                    if (
                        i.active == true &&
                        (i.showToAllSchools || i.schoolIds == undefined || i.schoolIds.indexOf(schoolId) > -1)
                    ) {
                        result.push({ ...i });
                    }
                });
                em.httpEnd();
                return result.sort((s1, s2) => s1.order - s2.order);
            },

            setActive(id: string, isActive: boolean) {
                patchState(store, updateEntity({ id, changes: { active: isActive } }));
                store.loaded.set(true);
            },

            hasPermission(id: string): boolean {
                if (
                    principal.hasAnyAuthorityDirect([
                        'ROLE_ADMIN',
                        'ROLE_TENANT_ADMIN',
                        'ROLE_AUDITOR',
                        'ROLE_TECHN_SUPPORT',
                        'ROLE_SYSTEM',
                        'ROLE_HEAD_TEACHER',
                    ])
                ) {
                    return true;
                }

                const gradeLevel: GradeLevel = store.entityMap()[id];
                if (gradeLevel?.active) {
                    return (
                        !gradeLevel.permissions ||
                        gradeLevel.permissions.length == 0 ||
                        gradeLevel.permissions.some(p => store.roles().includes(p))
                    );
                }
                return true;
            },

            clear(clearRoles: boolean) {
                store.loaded.set(false);
                console.log('GRADE-LEVEL-CLEAR');
                if (clearRoles) {
                    store.roles.set([]);
                }
                if (store.size() > 0) {
                    patchState(store, removeAllEntities());
                }
            },
        };
    }),
    withHooks({
        onInit: store => {
            const principal = inject(PrincipalService);
            const em = inject(EventManager);
            em.subscribe('school_grade_level_changed', content => {
                if (content.message.fromUser != principal.authenticatedUser.id) {
                    store.clear(true);
                }
            });
            principal.state$.pipe(takeUntilDestroyed()).subscribe(state => {
                if (state.authenticated) {
                    store.roles.set(state.user.roles);
                } else {
                    store.clear(true);
                }
            });
        },
        onDestroy: store => {
            store.clear(true);
            if (store.event()) {
                store.event().unsubscribe();
            }
        },
    })
);
